import React, { useState } from 'react';
import { RouteProp, useNavigation, useRoute } from '../../navigation/native';
import { KeyboardAvoidingView, Platform } from '../../web/react-native';
import { useDispatch } from 'react-redux';
import styled from '../../components/styled';

import { AppDispatch } from '../../store/store';
import { NavBar, NavTextButton, LoadingOverlay, TextField, RegularText } from '../../components';
import { selectEditedSnippets, selectIsLogDetailLoading, shiftLogActions } from '../../store';
import { RootStackParamList } from '../../app/Navigation';
import { displayConfirmationAlert, displayInformationAlert } from '../../helpers/Functions';
import { Route } from '../../app/Route';

const UpdatePercentageScreen = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigation = useNavigation();
  const { params } = useRoute<RouteProp<RootStackParamList, 'UpdatePercentageScreen'>>();

  const isLoading = selectIsLogDetailLoading.useHook();
  const snippet = selectEditedSnippets.useHook();

  const { completionPercent: currentPercentage = '', taskId } =
    snippet.data[params.rollupIndex ?? 0].rowData[params.rowIndex];

  const [percentage, setPercentage] = useState(currentPercentage as string);

  const percentageWithinBonds = +percentage >= 0 && +percentage <= 100;
  const hasError = !percentageWithinBonds;
  const canUpdate = !hasError && +percentage !== +currentPercentage && percentage !== '';

  const handleCancelPressed = () => {
    if (!canUpdate) {
      navigation.goBack();
    } else {
      displayConfirmationAlert({
          title: 'Confirm Cancel',
          description:
            'Are you sure you want leave? Any changes to the task completion percentage will be lost.',
          onConfirm: () => {
            navigation.goBack();
          },
        });
    }
  };

  const updatePercentage = () => {
    dispatch(
      shiftLogActions.updateTaskPercentage({
        taskId: taskId as string,
        percentCompleted: +percentage,
      }),
    )
      .unwrap()
      .then(() =>
        displayInformationAlert({
          title: 'Successfully Updated Completion Percentage',
          description: 'The task completion percentage has been successfully updated.',
          onOk: () => {
            navigation.goBack();
          },
        }),
      )
      .catch(() => {});
  };

  const handlePostPressed = () => {
    if (+percentage === 100) {
      displayConfirmationAlert({
        title: 'Confirm Complete',
        description:
          'Setting the competion percentage to 100 will complete the task. Would you like to proceed with task completion?',
        onConfirm: () =>
          navigation.navigate(Route.actionScreen, { ...params, action: 'Complete' }),
      });
    } else {
      updatePercentage();
    }
  };

  return (
    <>
      <NavBar
        title={'Task'}
        leftButton={<NavTextButton text="Cancel" onPress={handleCancelPressed} />}
        rightButton={
          <NavTextButton text="Update" onPress={handlePostPressed} disabled={!canUpdate} />
        }
      />
      <LoadingOverlay visible={isLoading} />
      <SafeAreaView>
        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          enabled
          keyboardVerticalOffset={100}>
          <Description>Percentage Completed</Description>
          <View>
            <TextInput
              error={hasError}
              autoFocus
              right
              onChangeText={setPercentage}
              placeholder="0"
              scrollEnabled={false}
              keyboardType={Platform.OS === 'android' ? 'numeric' : 'number-pad'}
              defaultValue={currentPercentage + ''}
            />
            <Text style={{ marginLeft: 5 }}>%</Text>
          </View>
          {hasError && <ErrorText>Percentage must be between 0 and 100.</ErrorText>}
        </KeyboardAvoidingView>
      </SafeAreaView>
    </>
  );
};

export default UpdatePercentageScreen;

const SafeAreaView = styled.SafeAreaView`
  flex: 1;
  margin: 10px;
  min-height: 10px;
`;

const Description = styled.Text`
  margin-top: 10px;
  font-weight: 500;
  margin-bottom: 7px;
`;

const View = styled.View`
  flex-direction: row;
  align-items: center;
`;

const TextInput = styled(TextField)`
  align-items: flex-end;
  max-height: 55px;
`;
const Text = styled(RegularText)`
  margin-right: 5px;
`;

const ErrorText = styled.Text`
  color: red;
  margin-top: 5px;
`;
